import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Loader2, MonitorSmartphone, Bug, X } from "lucide-react";
import { API } from "@/App";
import { tokenStore } from "@/lib/token";
import useClosing from "@/lib/useClosing";

// Petites fenêtres de Vanta AI : installer l'application, signaler un problème,
// confirmer une action (à la place de la fenêtre grise du navigateur).

function Modal({ title, icon: Icon, onClose, children, testId, closing: forced = false }) {
  const [closing, close] = useClosing(onClose);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [close]);
  return (
    <div className={`vai-overlay fixed inset-0 z-[75] bg-black/70 flex items-center justify-center p-4 ${closing || forced ? "is-closing" : ""}`} onMouseDown={close} role="dialog" aria-modal="true" aria-label={title}>
      <div className="vai-dialog w-full max-w-md rounded-2xl border border-white/10 bg-[#121218] shadow-2xl p-5" onMouseDown={(e) => e.stopPropagation()} data-testid={testId}>
        <div className="flex items-center gap-2 mb-4">
          <Icon className="w-5 h-5 text-white/70" />
          <h2 className="text-lg font-semibold flex-1">{title}</h2>
          <button type="button" onClick={close} className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/5" aria-label="Fermer"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Confirmation au style de Vanta AI : confirmDialog({ title, text, confirmLabel, danger }) renvoie
// une promesse (true / false). ConfirmHost est placé une fois dans la page.
let openConfirm = null;

export function confirmDialog(options) {
  if (!openConfirm) return Promise.resolve(window.confirm(options.text || options.title));
  return new Promise((resolve) => openConfirm({ ...options, resolve }));
}

export function ConfirmHost() {
  const [req, setReq] = useState(null);
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    openConfirm = (r) => { setLeaving(false); setReq(r); };
    return () => { openConfirm = null; };
  }, []);
  if (!req) return null;
  const close = (ok) => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => { req.resolve(ok); setReq(null); setLeaving(false); }, 160);
  };
  return (
    <Modal title={req.title} icon={AlertTriangle} onClose={() => close(false)} closing={leaving} testId="vai-confirm">
      {req.text && <p className="text-sm text-white/65">{req.text}</p>}
      <div className="flex justify-end gap-2 mt-5">
        <button type="button" onClick={() => close(false)} className="px-3 py-2 rounded-lg text-sm hover:bg-white/5" data-testid="vai-confirm-cancel">Annuler</button>
        <button type="button" autoFocus onClick={() => close(true)} data-testid="vai-confirm-ok"
          className={`px-4 py-2 rounded-lg text-sm font-medium ${req.danger ? "bg-red-600 hover:bg-red-500 text-white" : "bg-white text-black hover:bg-white/90"}`}>
          {req.confirmLabel || "Confirmer"}
        </button>
      </div>
    </Modal>
  );
}

const STEPS = [
  ["Ordinateur (Chrome, Edge)", "Icône d'installation à droite de la barre d'adresse, ou menu ⋮ > « Installer Vanta AI »."],
  ["iPhone et iPad (Safari)", "Bouton Partager, puis « Sur l'écran d'accueil »."],
  ["Android (Chrome)", "Menu ⋮, puis « Installer l'application » ou « Ajouter à l'écran d'accueil »."],
];

export function InstallDialog({ onClose, installed }) {
  return (
    <Modal title="Obtenir l'application" icon={MonitorSmartphone} onClose={onClose} testId="vai-install">
      {installed ? (
        <p className="text-sm text-white/70">Vanta AI est déjà installé sur cet appareil : ouvre-le depuis ton bureau ou ton écran d'accueil.</p>
      ) : (
        <>
          <p className="text-sm text-white/65 mb-4">Installe Vanta AI comme une application : il s'ouvre dans sa propre fenêtre, sans barre de navigateur.</p>
          <ul className="space-y-3">
            {STEPS.map(([where, how]) => (
              <li key={where} className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
                <p className="text-sm font-medium">{where}</p>
                <p className="text-xs text-white/55 mt-0.5">{how}</p>
              </li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
}

export function FeedbackDialog({ onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/support/tickets`, {
        subject: "Vanta AI : signalement",
        message: `${text.trim()}\n\n— Envoyé depuis Vanta AI (${window.location.pathname}, ${navigator.userAgent.slice(0, 120)})`,
      }, { headers: { Authorization: `Bearer ${tokenStore.get()}` } });
      toast.success("Merci ! L'équipe VantaPulse a reçu ton message (Support > Mes tickets).");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi impossible pour le moment");
      setBusy(false);
    }
  };
  return (
    <Modal title="Signaler un problème" icon={Bug} onClose={onClose} testId="vai-feedback-dialog">
      <p className="text-sm text-white/60 mb-3">Un bug, une réponse fausse, une idée ? Explique ce qui s'est passé : ton message arrive au support VantaPulse.</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={4000} autoFocus
        placeholder="Ex. : l'aperçu de l'artefact reste blanc quand…"
        className="w-full rounded-lg bg-white/[0.04] border border-white/10 px-3 py-2 text-sm outline-none focus:border-white/25 resize-y" data-testid="vai-feedback-text" />
      <div className="flex justify-end gap-2 mt-3">
        <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-sm hover:bg-white/5">Annuler</button>
        <button type="button" onClick={send} disabled={text.trim().length < 5 || busy} className="px-4 py-2 rounded-lg bg-white text-black text-sm font-medium disabled:opacity-40 inline-flex items-center gap-2" data-testid="vai-feedback-send">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}Envoyer
        </button>
      </div>
    </Modal>
  );
}
